import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Send, Loader2, Sparkles, Check, X } from "lucide-react";
import { api } from "@/lib/api-client";
import { useToast } from "@/lib/toast";

export function ReplyBox({
  onSend,
  isSending,
  inTakeover,
  pendingDraft,
  pendingDraftSeed,
}: {
  onSend: (text: string) => void;
  isSending: boolean;
  inTakeover: boolean;
  /**
   * Phase H7 - when `pendingDraftSeed` changes the textarea is replaced
   * with `pendingDraft`. Lets the HandoffLinkModal's "Insert into reply"
   * button push a wa.me link into the existing IG/Messenger reply box
   * without lifting the textarea's state into the parent.
   */
  pendingDraft?: string;
  pendingDraftSeed?: number;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const lastSeenSeed = useRef<number | undefined>(undefined);

  // AI Polish state - we store the proposed rewrite next to the draft so the
  // human can compare and accept/reject.
  const [polishing, setPolishing] = useState(false);
  const [polished, setPolished] = useState<string | null>(null);
  const [polishedLang, setPolishedLang] = useState<string | null>(null);

  useEffect(() => {
    if (
      pendingDraftSeed !== undefined &&
      pendingDraftSeed !== lastSeenSeed.current
    ) {
      lastSeenSeed.current = pendingDraftSeed;
      setText(pendingDraft ?? "");
      setPolished(null);
    }
  }, [pendingDraft, pendingDraftSeed]);

  function submit() {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText("");
    setPolished(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  async function runPolish() {
    const draft = text.trim();
    if (!draft) {
      toast.warn("Nothing to polish", "Type a draft first.");
      return;
    }
    setPolishing(true);
    try {
      const res = await api.post<{
        original: string;
        polished: string;
        language: string;
      }>("/api/ai/polish", { draft });
      setPolished(res.polished);
      setPolishedLang(res.language);
    } catch (e) {
      toast.error("AI polish failed", e instanceof Error ? e.message : String(e));
    } finally {
      setPolishing(false);
    }
  }

  function acceptPolish() {
    if (polished !== null) setText(polished);
    setPolished(null);
  }
  function rejectPolish() {
    setPolished(null);
  }

  return (
    <div className="space-y-2">
      {!inTakeover && (
        <div className="text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">
          Tip: the AI will also reply automatically on the next inbound. Click <strong>Take over</strong> if you want to handle this thread manually.
        </div>
      )}

      {/* AI polish proposal - shown above the textarea so it's the first thing
          the human reads. Accept replaces the draft; reject just dismisses. */}
      {polished !== null && (
        <div className="rounded-md border border-purple-200 bg-purple-50/60 p-2.5 space-y-2">
          <div className="flex items-center gap-2 text-[11px] text-purple-700">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-medium">AI-polished version</span>
            {polishedLang && (
              <span className="text-purple-500">· {polishedLang}</span>
            )}
          </div>
          <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">
            {polished}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={acceptPolish}>
              <Check className="h-3.5 w-3.5" /> Use this
            </Button>
            <Button size="sm" variant="ghost" onClick={rejectPolish}>
              <X className="h-3.5 w-3.5" /> Keep mine
            </Button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type your reply… (Ctrl/Cmd + Enter to send)"
          rows={3}
          className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none"
        />
        <div className="flex flex-col gap-1 self-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={runPolish}
            disabled={polishing || text.trim().length === 0}
            title="AI Polish: rewrite your draft to read natural and professional"
          >
            {polishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            <span className="hidden sm:inline">Polish</span>
          </Button>
          <Button
            onClick={submit}
            disabled={isSending || text.trim().length === 0}
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
